import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';

/**
 * Данные вкладки карточки пользователя. Запрос уходит, только когда вкладку открыли,
 * и не повторяется при переключении туда-обратно (компонент вкладки живёт, пока
 * открыта модалка).
 */
export function useUserTabData<T>(userId: string, fetcher: (id: string) => Promise<T>, errorText: string) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    fetcher(userId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e: any) => {
        if (!cancelled) toast.error(e?.response?.data?.message || errorText);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // fetcher и errorText — константы вызывающей стороны, перезапрос только при смене пользователя
  }, [userId]);

  return { data, loading };
}

export const formatDateTime = (s: string | Date | null | undefined) => {
  if (!s) return '—';
  return new Date(s).toLocaleString('ru-RU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatMoney = (amount: number, currency = 'UZS') =>
  new Intl.NumberFormat('uz-UZ', { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount);

export function TabLoader() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
    </div>
  );
}

export function EmptyState({ isDark, children }: { isDark: boolean; children: React.ReactNode }) {
  return (
    <div className={cn('text-center py-8 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>{children}</div>
  );
}

export function SectionTitle({ isDark, children }: { isDark: boolean; children: React.ReactNode }) {
  return (
    <h3 className={cn('text-base font-semibold mb-3', isDark ? 'text-white' : 'text-[#111111]')}>{children}</h3>
  );
}

export function StatCard({
  label,
  value,
  sublabel,
  isDark,
}: {
  label: string;
  value: React.ReactNode;
  sublabel?: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div className={cn('rounded-2xl p-4', isDark ? 'bg-[#0F0F17]' : 'bg-[#F5F3EF]')}>
      <div className={cn('text-xs font-semibold uppercase tracking-wider mb-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
        {label}
      </div>
      <div className={cn('text-xl font-bold', isDark ? 'text-white' : 'text-[#111111]')}>{value}</div>
      {sublabel && (
        <div className={cn('text-[11px] mt-0.5', isDark ? 'text-slate-500' : 'text-slate-400')}>{sublabel}</div>
      )}
    </div>
  );
}

export function RowCard({ isDark, children, className }: { isDark: boolean; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-xl px-4 py-3', isDark ? 'bg-[#0F0F17]' : 'bg-[#F5F3EF]', className)}>{children}</div>
  );
}

export function Muted({ isDark, children, className }: { isDark: boolean; children: React.ReactNode; className?: string }) {
  return <span className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500', className)}>{children}</span>;
}

/** Цветной бейдж статуса: tone задаёт цвет, текст — label. */
const TONES = {
  green: 'bg-emerald-500/10 text-emerald-500',
  amber: 'bg-amber-500/10 text-amber-500',
  red: 'bg-red-500/10 text-red-500',
  blue: 'bg-blue-500/10 text-blue-500',
  slate: 'bg-slate-500/10 text-slate-500',
  purple: 'bg-purple-500/10 text-purple-500',
} as const;
export type Tone = keyof typeof TONES;

export function StatusPill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span className={cn('inline-flex items-center rounded-lg px-2 py-0.5 text-xs font-medium', TONES[tone])}>
      {children}
    </span>
  );
}
